import React, { useRef, useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

/**
 * Renders text content with special formatting and search term highlighting
 * @param {string} text - The text content to format
 * @param {string} searchTerm - Term to highlight in the text (optional)
 * @param {Function} highlightText - Function to apply highlighting to matched text
 * @returns {Array|null} Array of formatted React elements or null if no text
 */
const renderFormattedText = (text, searchTerm, highlightText) => {
  // Return null if no text provided
  if (!text) return null;

  // Split text into lines for processing
  const lines = text.split('\n');
  return lines.map((line, lineIndex) => {
    const lineKey = `line-${lineIndex}`;
    let content;

    // Handle bullet point lines starting with "• "
    if (line.startsWith('• ')) {
      content = (
        <div key={lineKey} className="flex">
          <span className="w-4 inline-block">•</span>
          <span>{searchTerm ? highlightText(line.substring(2), searchTerm) : line.substring(2)}</span>
        </div>
      );
    } 
    // Handle indented lines starting with spaces
    else if (line.startsWith('    ')) {
      content = (
        <div key={lineKey} className="pl-4">
          {searchTerm ? highlightText(line.trim(), searchTerm) : line.trim()}
        </div>
      );
    }
    // Handle regular lines with no special formatting
    else {
      content = (
        <div key={lineKey}>
          {searchTerm ? highlightText(line, searchTerm) : line}
        </div>
      );
    }

    return content;
  });
};

const useResizeText = (content, containerRef, minSize = 8, maxSize = 12, startSize = 12) => {
  const [fontSize, setFontSize] = useState(startSize);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeText = () => {
      const container = containerRef.current;
      let size = startSize;
      container.style.fontSize = `${size}px`;

      if (container.scrollHeight <= container.clientHeight) {
        // Text fits, try to increase font size
        while (size < maxSize) {
          size++;
          container.style.fontSize = `${size}px`;
          if (container.scrollHeight > container.clientHeight) {
            size--;
            container.style.fontSize = `${size}px`;
            break;
          }
        }
      } else {
        // Text doesn't fit, decrease font size
        while (size > minSize && container.scrollHeight > container.clientHeight) {
          size--;
          container.style.fontSize = `${size}px`;
        }
      }

      setFontSize(size);
    };

    resizeText();
    window.addEventListener('resize', resizeText);
    return () => window.removeEventListener('resize', resizeText);
  }, [content, containerRef, minSize, maxSize, startSize]);

  return fontSize;
};

// Updated color utility functions with proper error handling
function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') {
    return { r: 152, g: 31, b: 43 }; // Default cranberry color
  }

  try {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 152, g: 31, b: 43 }; // Default cranberry color
  } catch (error) {
    console.warn('Invalid hex color:', hex);
    return { r: 152, g: 31, b: 43 }; // Default cranberry color
  }
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function darkenColor(color, amount = 30) {
  if (!color || typeof color !== 'string') {
    return '#981F2B'; // Default cranberry color
  }

  try {
    const { r, g, b } = hexToRgb(color);
    const factor = (100 - amount) / 100;
    const newR = Math.floor(Math.max(0, r * factor));
    const newG = Math.floor(Math.max(0, g * factor));
    const newB = Math.floor(Math.max(0, b * factor));
    return rgbToHex(newR, newG, newB);
  } catch (error) {
    console.warn('Error darkening color:', error);
    return '#981F2B'; // Default cranberry color
  }
}
// TopicItem component displays a topic with its name and highlighted concept
// Props:
// - topic: The topic object containing name, concepts, and tag information
// - searchTerm: Term to highlight in the topic name and concept text
// - highlightText: Function to handle text highlighting
export const TopicItem = ({ topic, searchTerm, highlightText }) => {
  const { isPremium, isLoading } = usePremium();
  const topicLocked = isLocked(topic, isPremium, isLoading);
  // Get the highlighted concept or fall back to first concept if none highlighted

  const highlightedConcept = topic.concepts?.find(concept => concept.is_highlighted) || 
                            (topic.concepts?.length > 0 ? topic.concepts[0] : null);

  // Reference for the container element to handle text resizing
  const containerRef = useRef(null);

  // Calculate dynamic font size based on content length
  const fontSize = useResizeText(
    topic.name + (highlightedConcept?.text || ''), 
    containerRef, 
    8, // minimum font size 
    12, // maximum font size
    12  // starting font size
  );

  // Get tag color with fallbacks and calculate derived colors
  const tagColor = topic?.tag?.color || topic?.tags?.color || '#981F2B'; // Default to cranberry
  const backgroundColor = tagColor ? `${tagColor}70` : 'rgba(229, 231, 235, 0.7)'; // 70 = 44% opacity
  const borderColor = tagColor ? darkenColor(tagColor) : '#d1d5db';

  return (
    <div 
      ref={containerRef}
      className="flex flex-col items-center w-full h-full p-1 overflow-hidden rounded-md"
      style={{ 
        fontSize: `${fontSize}px`,
      }}
    >
      {/* Topic name display */}
      <div className="w-full mb-1 flex justify-center">
        <span 
          className="inline-block px-2 py-1 rounded-full font-medium text-black w-auto max-w-full break-words text-center"
          style={{ 
            backgroundColor: backgroundColor,
            border: `1px solid ${borderColor}`,
            lineHeight: '1.2',
          }}
        >
          {searchTerm ? highlightText(topic.name, searchTerm) : topic.name}
        </span>
      </div>

      {/* Relationship description - distinguishes from difficult concept */}
      {topicLocked ? (
        <p className="w-full max-w-[90%] text-center text-xs text-neutral-500 mb-1 leading-tight inline-flex items-center justify-center gap-1">
          <Lock size={11} aria-hidden="true" />
          Premium connection
        </p>
      ) : topic.relation_description ? (
        <p className="w-full max-w-[90%] text-center text-xs italic text-neutral-600 mb-1 leading-tight">
          &ldquo;{searchTerm ? highlightText(topic.relation_description, searchTerm) : topic.relation_description}&rdquo;
        </p>
      ) : null}

      {/* Highlighted concept display */}
      {highlightedConcept && (
        <div className="w-full flex-grow flex justify-center">
          <div 
            className="w-auto max-w-[90%] text-left leading-tight" 
            style={{ 
              color: highlightedConcept.is_highlighted ? '#0066cc' : '#666666' 
            }}
          >
            {renderFormattedText(highlightedConcept.text, searchTerm, highlightText)}
          </div>
        </div>
      )}
    </div>
  );
};
